import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetChargingSessionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/charging-sessions/get-charging-sessions-paginated.model';
import { GatewayChargingSessionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/charging-sessions/gateway-charging-session.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';
import React from 'react';

import { EmspBoChargingSessionListDurationColumn } from './emsp-bo-charging-session-list-duration.column';
import { EmspBoChargingSessionListEmspUserDidColumn } from './emsp-bo-charging-session-list-emsp-user-did.column';
import { EmspBoChargingSessionListEmspUserEmailAddressColumn } from './emsp-bo-charging-session-list-emsp-user-email-address.column';
import { EmspBoChargingSessionListEmspUserFirstNameColumn } from './emsp-bo-charging-session-list-emsp-user-first-name.column';
import { EmspBoChargingSessionListEmspUserIdColumn } from './emsp-bo-charging-session-list-emsp-user-id.column';
import { EmspBoChargingSessionListEmspUserLastNameColumn } from './emsp-bo-charging-session-list-emsp-user-last-name.column';
import { EmspBoChargingSessionListEnergyDeliveredColumn } from './emsp-bo-charging-session-list-energy-delivered.column';
import { EmspBoChargingSessionListLocationNameColumn } from './emsp-bo-charging-session-list-location-name.column';
import { EmspBoChargingSessionListLocationOperatorNameColumn } from './emsp-bo-charging-session-list-location-operator-name.column';
import { EmspBoChargingSessionListLocationOwnerNameColumn } from './emsp-bo-charging-session-list-location-owner-name.column';
import { EmspBoChargingSessionListLocationSubOperatorNameColumn } from './emsp-bo-charging-session-list-location-sub-operator-name.column';
import { EmspBoChargingSessionListSessionIdColumn } from './emsp-bo-charging-session-list-session-id.column';
import { EmspBoChargingSessionListStartedAtColumn } from './emsp-bo-charging-session-list-started-at.column';
import { EmspBoChargingSessionListStatusColumn } from './emsp-bo-charging-session-list-status.column';
import { EmspBoChargingSessionListTokenUidColumn } from './emsp-bo-charging-session-list-token-uid.column';
import { EmspBoChargingSessionListTotalTtcColumn } from './emsp-bo-charging-session-list-total-ttc.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const EMSP_BO_CHARGING_SESSION_LIST_SORT_FIELD: NonNullable<
  GetChargingSessionsPaginatedModel['sortField']
> = 'startedAt';

export const EMSP_BO_CHARGING_SESSION_LIST_SORT_ORDER: NonNullable<
  GetChargingSessionsPaginatedModel['sortOrder']
> = -1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetChargingSessionsPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const EMSP_BO_CHARGING_SESSION_LIST_FILTER: ResourceListFilter =
  {} as const;

export const EMSP_BO_CHARGING_SESSION_LIST_FILTER_NAME_LIST = Object.keys(
  EMSP_BO_CHARGING_SESSION_LIST_FILTER,
) as Array<keyof typeof EMSP_BO_CHARGING_SESSION_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const emspBoChargingSessionListColumnHelper =
  createColumnHelper<GatewayChargingSessionModel>();

type ResourceColumnType = ColumnDef<GatewayChargingSessionModel, any>[];

export const EMSP_BO_CHARGING_SESSION_LIST_COLUMN_LIST: ResourceColumnType = [
  emspBoChargingSessionListColumnHelper.accessor(
    'emspUser.id',
    EmspBoChargingSessionListEmspUserIdColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'emspUser.did',
    EmspBoChargingSessionListEmspUserDidColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'emspUser.firstName',
    EmspBoChargingSessionListEmspUserFirstNameColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'emspUser.lastName',
    EmspBoChargingSessionListEmspUserLastNameColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'emspUser.emailAddress',
    EmspBoChargingSessionListEmspUserEmailAddressColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'sessionId',
    EmspBoChargingSessionListSessionIdColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'tokenUid',
    EmspBoChargingSessionListTokenUidColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'locationName',
    EmspBoChargingSessionListLocationNameColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'locationOperatorName',
    EmspBoChargingSessionListLocationOperatorNameColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'locationOwnerName',
    EmspBoChargingSessionListLocationOwnerNameColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'locationSubOperatorName',
    EmspBoChargingSessionListLocationSubOperatorNameColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'startedAt',
    EmspBoChargingSessionListStartedAtColumn,
  ),
  emspBoChargingSessionListColumnHelper.display(
    EmspBoChargingSessionListDurationColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'status',
    EmspBoChargingSessionListStatusColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'energyDelivered',
    EmspBoChargingSessionListEnergyDeliveredColumn,
  ),
  emspBoChargingSessionListColumnHelper.accessor(
    'totalTTC',
    EmspBoChargingSessionListTotalTtcColumn,
  ),
];

export const EMSP_BO_CHARGING_SESSION_LIST_COLUMN_ID_LIST =
  EMSP_BO_CHARGING_SESSION_LIST_COLUMN_LIST.flatMap((columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  });

export const EMSP_BO_CHARGING_SESSION_LIST_COLUMN_ORDER = [
  ...EMSP_BO_CHARGING_SESSION_LIST_COLUMN_ID_LIST,
];

export const EMSP_BO_CHARGING_SESSION_LIST_COLUMN_VISIBILITY: VisibilityState =
  EMSP_BO_CHARGING_SESSION_LIST_COLUMN_ID_LIST.reduce(
    (acc, id) => ({ ...acc, [id]: true }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoChargingSessionListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const EMSP_BO_CHARGING_SESSION_LIST_DEFAULT_OPTIONS: EmspBoChargingSessionListOptionsModel =
  {
    columnVisibility: EMSP_BO_CHARGING_SESSION_LIST_COLUMN_VISIBILITY,
    columnOrder: EMSP_BO_CHARGING_SESSION_LIST_COLUMN_ORDER,
  };

export const EMSP_BO_CHARGING_SESSION_LIST_COLUMN_LABEL_I18N_KEY: Record<
  string,
  string
> = {
  emspUserId: 'delmonicos-shared-sdk.outputs-emsp-user.id',
  emspUserDid: 'delmonicos-shared-sdk.outputs-emsp-user.did',
  emspUserFirstName: 'delmonicos-shared-sdk.outputs-emsp-user.first-name',
  emspUserLastName: 'delmonicos-shared-sdk.outputs-emsp-user.last-name',
  emspUserEmailAddress: 'delmonicos-shared-sdk.outputs-emsp-user.email-address',
  sessionId: 'delmonicos-shared-sdk.outputs-charging-session.session-id',
  tokenUid: 'delmonicos-shared-sdk.outputs-charging-session.token-uid',
  locationName: 'delmonicos-shared-sdk.outputs-charging-session.location-name',
  locationOperatorName:
    'delmonicos-shared-sdk.outputs-charging-session.location-operator-name',
  locationOwnerName:
    'delmonicos-shared-sdk.outputs-charging-session.location-owner-name',
  locationSubOperatorName:
    'delmonicos-shared-sdk.outputs-charging-session.location-sub-operator-name',
  startedAt: 'delmonicos-shared-sdk.outputs-charging-session.started-at',
  duration: 'delmonicos-shared-sdk.outputs-charging-session-alt.duration',
  status: 'delmonicos-shared-sdk.outputs-charging-session.status',
  energyDelivered:
    'delmonicos-shared-sdk.outputs-charging-session.energy-delivered',
  totalTTC: 'delmonicos-shared-sdk.outputs-charging-session.total-ttc',
};
